import { Copy, Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCopyToClipboard } from '@reflex/ui/hooks/useCopyToClipboard';
import { cn } from '../../../lib/utils';
import { CHAT_SIZE_CLASSES, type ChatSize } from './sizes';

export function CodeCopyButton({ code, testId }: { code: string; testId?: string }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <button
      onClick={() => copy(code)}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-3xs text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
      aria-label="Copy code"
      data-testid={testId}
    >
      {copied ? (
        <>
          <Check className="h-3 w-3 text-status-success" />
          <span>Copied</span>
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" />
          <span>Copy</span>
        </>
      )}
    </button>
  );
}

/**
 * Shared wrapper + header for prose-sized fenced blocks: `ProseCodeBlock`
 * (plain fences), the Shiki-highlighted block in `plugin-docs`, and
 * `MermaidBlock`. Owns the surrounding card chrome, the language/filename
 * label, and the copy button so the variants stay visually identical (same
 * padding, same header layout).
 *
 * `actions` renders between the label and the copy button, for blocks that
 * carry their own controls (the mermaid diagram/source toggle).
 */
export function CodeBlockShell({
  lang,
  filename,
  code,
  className = '',
  testId,
  copyTestId,
  actions,
  children,
}: {
  lang?: string;
  filename?: string;
  code: string;
  className?: string;
  testId?: string;
  copyTestId?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const showFilename = Boolean(filename);
  const showLangChip = showFilename && Boolean(lang);
  const showLangLabel = !showFilename && Boolean(lang);
  return (
    <div
      className={`my-6 overflow-hidden rounded-xl border bg-muted/30 ${className}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/40 px-4 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {showFilename && (
            <span className="truncate font-mono text-[11px] text-foreground/80">{filename}</span>
          )}
          {showLangLabel && (
            <span className="truncate font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              {lang}
            </span>
          )}
          {showLangChip && (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {lang}
            </span>
          )}
        </div>
        {actions}
        <CodeCopyButton code={code} testId={copyTestId} />
      </div>
      {children}
    </div>
  );
}

/**
 * The chat-density counterpart to `CodeBlockShell`, used by plain fences in
 * chat markdown and by `MermaidBlock` at `sm`/`xs`. Padding and type scale
 * come from `CHAT_SIZE_CLASSES` so a mermaid fence and the code fence above it
 * share one spacing source.
 */
export function ChatCodeBlockShell({
  size,
  lang,
  code,
  className,
  testId,
  actions,
  children,
}: {
  size: ChatSize;
  lang?: string;
  code: string;
  className?: string;
  testId?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const s = CHAT_SIZE_CLASSES[size];
  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-border/50 bg-muted/20 last:mb-0',
        s.spacing.body,
        className,
      )}
      data-testid={testId}
    >
      <div
        className={cn(
          'flex items-center gap-2 border-b border-border/50 bg-muted/30',
          s.container.headerPad,
        )}
      >
        {lang ? (
          <span className="flex-1 truncate font-mono text-3xs text-muted-foreground">{lang}</span>
        ) : (
          <span className="flex-1" />
        )}
        {actions}
        <CodeCopyButton code={code} />
      </div>
      {children}
    </div>
  );
}

/** Body text classes for a prose-sized code block. */
export const PROSE_CODE_TEXT = 'px-4 py-3 text-[13px] leading-[1.6] text-foreground/90';

/** Body text classes for a chat-sized code block. */
export function chatCodeText(size: ChatSize): string {
  const s = CHAT_SIZE_CLASSES[size];
  return cn(s.container.codePad, s.blockCode, 'leading-relaxed text-foreground/85');
}

/** The scrollable, focusable `<pre>` every code body renders into. */
export function CodeText({ code, className }: { code: string; className?: string }) {
  return (
    <pre tabIndex={0} className={cn('overflow-x-auto font-mono', className)}>
      <code>{code}</code>
    </pre>
  );
}

export function ProseCodeBlock({ code, lang }: { code: string; lang?: string }) {
  return (
    <CodeBlockShell code={code} lang={lang}>
      <CodeText code={code} className={PROSE_CODE_TEXT} />
    </CodeBlockShell>
  );
}

export function ProseInlineCode({ children }: { children: ReactNode }) {
  return (
    <code className="rounded-md border border-border/50 bg-muted/70 px-[0.4em] py-[0.15em] font-mono text-[0.85em] text-foreground">
      {children}
    </code>
  );
}
